import { Localized } from "@fluent/react"
import { Alert, Autocomplete, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Stack, TextField } from "@mui/material"
import { useState } from "react"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { If } from "../../../app/If"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { createCompanySSOSetting, SysCompanyTenant, updateCompanySSOSetting } from "./CompanySSOApi"

const providerOptions = ["Microsoft"]

interface CreateEditCompanySSOSettingsDialogProps {
    mode: "create" | "edit";
    companyId: string;
    companyTenant?: SysCompanyTenant;
    onSaveSuccess: (companyTenant: SysCompanyTenant) => void;
    onCancel: () => void;
}

export const CreateEditCompanySSOSettingsDialog: React.FC<CreateEditCompanySSOSettingsDialogProps> = ({ mode, companyId, companyTenant, onSaveSuccess, onCancel }) => {
    const dispatch = useAppDispatch()

    const [provider, setProvider] = useState<string>(companyTenant?.provider ?? "")
    const [tenantId, setTenantId] = useState<string>(companyTenant?.tenantId ?? "")
    const [tenantName, setTenantName] = useState<string>(companyTenant?.tenantName ?? "")
    const [defaultDomain, setDefaultDomain] = useState<string>(companyTenant?.defaultDomain ?? "")
    const [isActive, setIsActive] = useState<boolean>(companyTenant?.isActive ?? true)
    const [allowSelfRegistration, setAllowSelfRegistration] = useState<boolean>(companyTenant?.allowSelfRegistration ?? false)
    const [allowExternalUsers, setAllowExternalUsers] = useState<boolean>(companyTenant?.allowExternalUsers ?? false)

    const [serverError, setServerError] = useState("")
    const [saving, setSaving] = useState<boolean>(false)

    const handleConfirm = async () => {
        try {
            setSaving(true)

            const payload = {
                id: companyTenant?.id,
                companyId: Number(companyId),
                provider,
                tenantId,
                tenantName,
                defaultDomain,
                isActive,
                allowSelfRegistration,
                allowExternalUsers,
            }

            if (mode === "create") {
                const result = await dispatch(createCompanySSOSetting(payload))

                if (createCompanySSOSetting.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onSaveSuccess(result.payload)
                } else {
                    let errorMsg = "error"

                    if (result.payload && result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }

                    setServerError(errorMsg)
                }
            } else if (mode === "edit" && companyTenant) {
                const result = await dispatch(updateCompanySSOSetting(payload))

                if (updateCompanySSOSetting.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onSaveSuccess(result.payload)
                } else {
                    let errorMsg = "error"

                    if (result.payload && result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }

                    setServerError(errorMsg)
                }
            }
        }
        catch {
            dispatch(showError("error"))
        }
        finally {
            setSaving(false)
        }
    }

    return (
        <>
            <DialogTitle>
                <If condition={mode === "create"} otherwise={
                    <Localized id="company-sso-settings-edit">
                        <span>_Edytuj ustawienia SSO</span>
                    </Localized>
                }>
                    <Localized id="company-sso-settings-new">
                        <span>_Dodaj ustawienia SSO</span>
                    </Localized>
                </If>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2} padding={2}>
                    <Autocomplete
                        freeSolo
                        options={providerOptions}
                        value={provider}
                        onInputChange={(_, value) => setProvider(value)}
                        onChange={(_, value) => setProvider(value ?? "")}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                label={<Localized id="company-sso-provider">_Dostawca</Localized>}
                            />
                        )}
                    />
                    <TextField
                        fullWidth
                        value={tenantId}
                        onChange={(event) => setTenantId(event.target.value)}
                        label={<Localized id="company-sso-tenant-id">_Tenant ID</Localized>}
                    />
                    <TextField
                        fullWidth
                        value={tenantName}
                        onChange={(event) => setTenantName(event.target.value)}
                        label={<Localized id="company-sso-tenant-name">_Nazwa tenantu</Localized>}
                    />
                    <TextField
                        fullWidth
                        value={defaultDomain}
                        onChange={(event) => setDefaultDomain(event.target.value)}
                        label={<Localized id="company-sso-default-domain">_Domena domyślna</Localized>}
                    />
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />}
                            label={<Localized id="company-sso-is-active"><span>_Aktywne</span></Localized>}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={allowSelfRegistration} onChange={(event) => setAllowSelfRegistration(event.target.checked)} />}
                            label={<Localized id="company-sso-allow-self-registration"><span>_Samorejestracja</span></Localized>}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={allowExternalUsers} onChange={(event) => setAllowExternalUsers(event.target.checked)} />}
                            label={<Localized id="company-sso-allow-external-users"><span>_Użytkownicy zewnętrzni</span></Localized>}
                        />
                    </FormGroup>
                </Stack>
                {
                    serverError && <Alert sx={{ marginTop: 1 }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleConfirm} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </>
    )
}